import {
  AspAgingBucketKey,
  AspBill,
  AspInvoice,
  BillOrInvoiceRailz,
  Box,
  CardTabs,
  Timestamp,
  Title,
  Tooltip,
} from 'vopay';
import { FunctionComponent, useState, useEffect, useMemo, useRef } from 'react';
import {
  format,
  subMonths,
  startOfMonth,
  endOfMonth,
  subQuarters,
  startOfQuarter,
  endOfQuarter,
  subDays,
  startOfWeek,
  endOfWeek,
  compareAsc,
  parseISO,
} from 'date-fns';
import AccountingSystemSearchForm, {
  SearchParams,
  Currency,
  TimeRange,
  StatusFilter,
  DateFilterType,
} from './AccountingSystemSearchForm';
import { getAgingBucketRange, isAgingBucketKey } from './AgingOverview/agingSummary.helper';
import AccountingSystemClosed, { getLastPaymentDate } from './AccountingSystemClosed/AccountingSystemClosed';
import AccountingSystemOpen from './AccountingSystemOpen/AccountingSystemOpen';
import AccountingSystemTotals from './AccountingSystemTotals';
import AccountingSystemExport from './AccountingSystemExport/AccountingSystemExport';
import { ContactDetailDrawer } from '#shared/components/Contacts';
import AgingOverview from './AgingOverview/AgingOverview';
import useFetchAspInvoicesSummary from '#shared/hooks/useApi/useFetchAspInvoicesSummary';
import useFetchAspBillsSummary from '#shared/hooks/useApi/useFetchAspBillsSummary';
import useFetchAspAccountAssignment from '#shared/hooks/useApi/useFetchAspAccountAssignment';
import useTriggerAspSync from '#shared/hooks/useApi/useTriggerAspSync';
import useFetchAspSyncStatus from '#shared/hooks/useApi/useFetchAspSyncStatus';
import useFetchAspConnections from '#shared/hooks/useApi/useFetchAspConnections';
import useFetchAspInvoices from '#shared/hooks/useApi/useFetchAspInvoices';
import useFetchAspBills from '#shared/hooks/useApi/useFetchAspBills';
import useFetchAspCustomers from '#shared/hooks/useApi/useFetchAspCustomers';
import useFetchAspVendors from '#shared/hooks/useApi/useFetchAspVendors';
import { toAccountingRow, toAccountingBillRow } from './aspAccountingAdapter';
import { useToast } from '#shared/providers/Toast';
import AccountingSystemDialogue from './AccountingSystemDialogue/AccountingSystemDialogue';
import AccountingSystemContext, { AccountingSystemCtx } from './AccountingSystemDialogue/AccountingSystemContext';
import { AccountingSystemDialogueHandler, DialogueHandlerProps } from './AccountingSystemDialogue/types';
import styles from './AccountingSystem.module.scss';
import { useBlockingLoader } from '#shared/providers/Loader';
import useFetchAccountBalance from '#shared/hooks/useApi/useFetchAccountBalance';
import {
  AccountingSoftwareProvider,
  AccountingSoftwareAccount,
  AccountingSoftwareHostActions,
  AccountingSoftwareUserPermissions,
} from './AccountingSoftwareContext';
import { Trans, useTranslation } from 'react-i18next';
import { CircleButton } from 'vopay';

import ConnectAccountingSoftware from './Connect/ConnectAccountingSoftware';
import { Button, TableFiltersContainer } from '#shared/components/UI';

type AccountingType = 'Payable' | 'Receivable';
type Tab = 'open' | 'closed';

type QueryParams = {
  tab: string | null;
  currency: string | null;
  searchTerm: string | null;
  timeRange: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string | null;
  dateFilterType: string | null;
  agingBucket: string | null;
};

// The list filters an aging bucket displaces when selected, so deselecting it
// lands the user back where they were.
type AgingBucketSnapshot = Pick<QueryParams, 'timeRange' | 'startDate' | 'endDate' | 'currency'>;

export type AccountingSoftwareHostProps = {
  Type: AccountingType;
  // Scopes every list/summary fetch to one accounting contact (a host's
  // contact detail tabs); hides the page chrome and the contact column.
  ContactID?: number;
  // Facts about the account, identical for every user of it. Country alone
  // supports the read experience; the capability flags feed the action layer.
  account: AccountingSoftwareAccount;
  // Absent = read-only (the default for the embed until it carries a
  // permission snapshot).
  userPermissions?: AccountingSoftwareUserPermissions;
  hostActions?: AccountingSoftwareHostActions;
  // Enables the in-place connect/setup flow when no software is connected.
  // Absent = a message pointing at the VoPay portal (hosts that can't run
  // the Railz OAuth flow, e.g. third-party embeds without a widget id).
  connect?: {
    railzWidgetId: string;
    redirectURL: string;
  };
};

// A sync is polled once a minute; give up (and re-enable the button) after
// this many checks so a stuck or failed sync doesn't disable syncing for the
// rest of the session.
const MAX_SYNC_POLL_CYCLES = 15;

type FC = FunctionComponent<AccountingSoftwareHostProps>;

const AccountingSystemContainer: FC = ({
  Type,
  connect,
  ContactID,
  account,
  userPermissions,
  hostActions,
}: AccountingSoftwareHostProps) => {
  const isContactScoped = ContactID !== undefined;

  const { fetchAspAccountAssignment } = useFetchAspAccountAssignment();
  const blockingLoader = useBlockingLoader();
  const { balanceResponse, fetchAccountBalance } = useFetchAccountBalance();
  const { triggerAspSync } = useTriggerAspSync();
  const { syncStatus, fetchAspSyncStatus } = useFetchAspSyncStatus();
  const { connections, fetchAspConnections } = useFetchAspConnections();
  const { fetchAspInvoices } = useFetchAspInvoices();
  const { fetchAspBills } = useFetchAspBills();
  const { fetchAspCustomers } = useFetchAspCustomers();
  const { fetchAspVendors } = useFetchAspVendors();

  const {
    summaries: invoiceSummaries,
    fetching: fetchingInvoiceSummaries,
    fetchAspInvoicesSummary,
  } = useFetchAspInvoicesSummary();

  const { summaries: billSummaries, fetching: fetchingBillSummaries, fetchAspBillsSummary } = useFetchAspBillsSummary();
  const toast = useToast();
  const [fetchingStatus, setFetchingStatus] = useState(true);
  const [pollCycle, setPollCycle] = useState(0);
  const [isSyncingData, setIsSyncingData] = useState(false);
  const [showConnect, setShowConnect] = useState(false);

  // The clicked row's linked record, shown in the contact drawer.
  const [contactDrawerAccountId, setContactDrawerAccountId] = useState<string>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastSync, setLastSync] = useState('');
  const [railzBillsOrInvoices, setRailzBillsOrInvoices] = useState<BillOrInvoiceRailz[]>([]);
  const [BillsOrInvoices, setBillsOrInvoices] = useState<BillOrInvoiceRailz[]>([]);
  const [railzBillsOrInvoicesFiltered, setRailzBillsOrInvoicesFiltered] = useState<BillOrInvoiceRailz[]>([]);
  const [BillsOrInvoicesFiltered, setBillsOrInvoicesFiltered] = useState<BillOrInvoiceRailz[]>([]);

  const country = account.Country;
  const actionsEnabled = userPermissions?.payAndCollect ?? false;

  // Filters live in memory: the iframe URL is minted by the host and carries
  // the single-use token, so it is not a place to persist filter state.
  const [embeddedParams, setEmbeddedParams] = useState<Partial<QueryParams>>({});

  const updateQueryParams = (newParams: Partial<QueryParams>) => {
    setEmbeddedParams((prev) => ({ ...prev, ...newParams }));
  };

  // Helper function to parse query params
  const getQueryParams = () => {
    const params = new URLSearchParams('');

    {
      Object.entries(embeddedParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') params.set(key, value);
      });
    }

    const agingBucketParam = params.get('agingBucket');

    return {
      tab: (params.get('tab') as Tab) || 'open',
      currency: (params.get('currency') as Currency) || '',
      searchTerm: params.get('searchTerm') || '',
      timeRange: (params.get('timeRange') as TimeRange) || 'custom',
      startDate: params.get('startDate')
        ? (Number(params.get('startDate')) as Timestamp)
        : (startOfMonth(subMonths(new Date(), 1)).getTime() as Timestamp),
      endDate: params.get('endDate')
        ? (Number(params.get('endDate')) as Timestamp)
        : (endOfMonth(new Date()).getTime() as Timestamp),
      status: (params.get('status') as StatusFilter) || '',
      dateFilterType: (params.get('dateFilterType') as DateFilterType) || 'closedDate',
      agingBucket: isAgingBucketKey(agingBucketParam) ? agingBucketParam : null,
    };
  };

  // Helper function to calculate date range from time range key
  const calculateDateRange = (
    timeRangeKey: TimeRange,
    startDate: Timestamp | undefined,
    endDate: Timestamp | undefined,
  ): { startDate: Date | undefined; endDate: Date | undefined } => {
    const today = new Date();
    let adjustedStartDate: Date | undefined;
    let adjustedEndDate: Date | undefined;

    switch (timeRangeKey) {
      case 'today':
        adjustedStartDate = today;
        adjustedEndDate = today;
        break;
      case 'yesterday':
        adjustedStartDate = subDays(today, 1);
        adjustedEndDate = adjustedStartDate;
        break;
      case 'thisWeek':
        adjustedStartDate = startOfWeek(today);
        adjustedEndDate = endOfWeek(today);
        break;
      case 'lastWeek':
        adjustedStartDate = startOfWeek(subDays(today, 7));
        adjustedEndDate = endOfWeek(subDays(today, 7));
        break;
      case 'thisMonth':
        adjustedStartDate = startOfMonth(today);
        adjustedEndDate = endOfMonth(today);
        break;
      case 'lastMonth':
        adjustedStartDate = startOfMonth(subMonths(today, 1));
        adjustedEndDate = endOfMonth(subMonths(today, 1));
        break;
      case 'thisQuarter':
        adjustedStartDate = startOfQuarter(today);
        adjustedEndDate = endOfQuarter(today);
        break;
      case 'lastQuarter':
        adjustedStartDate = startOfQuarter(subQuarters(today, 1));
        adjustedEndDate = endOfQuarter(subQuarters(today, 1));
        break;
      case 'last6Months':
        adjustedStartDate = startOfMonth(subMonths(today, 6));
        adjustedEndDate = endOfMonth(today);
        break;
      case 'last12Months':
        adjustedStartDate = startOfMonth(subMonths(today, 12));
        adjustedEndDate = endOfMonth(today);
        break;
      case 'allTime':
        // No window at all: the asp endpoints treat StartDate/EndDate as
        // optional and drop the date clause when they are absent.
        adjustedStartDate = undefined;
        adjustedEndDate = undefined;
        break;
      case 'custom':
        // Return dates from URL params for custom
        return { startDate: new Date(Number(startDate)), endDate: new Date(Number(endDate)) };
      default:
        // Return dates from URL params for any other value (shouldn't happen with strict typing)
        return { startDate: new Date(Number(startDate)), endDate: new Date(Number(endDate)) };
    }

    return { startDate: adjustedStartDate, endDate: adjustedEndDate };
  };

  // Derive state from query params using useMemo
  const queryParams = useMemo(() => getQueryParams(), [embeddedParams]);

  const activeTab = useMemo<'open' | 'closed'>(() => queryParams.tab || 'open', [queryParams.tab]);
  const currency = useMemo(() => queryParams.currency as Currency, [queryParams.currency]);
  const searchTerm = useMemo(() => queryParams.searchTerm, [queryParams.searchTerm]);
  const timeRange = useMemo(() => queryParams.timeRange, [queryParams.timeRange]);
  const status = useMemo(() => queryParams.status as StatusFilter, [queryParams.status]);

  const dateFilterType = useMemo(() => queryParams.dateFilterType as DateFilterType, [queryParams.dateFilterType]);

  // Currency the totals header shows a zero in when no rows match: the
  // page's currency filter if applied, else the account's home currency.
  const emptyStateCurrency = (currency && currency !== 'all' ? currency : null) || (country === 'US' ? 'USD' : 'CAD');

  // Aging overview (§6.1): all-time open totals per currency, independent of
  // the list filters — shown one currency at a time (ID-13873).
  const agingSummaries = Type === 'Receivable' ? invoiceSummaries : billSummaries;
  const fetchingAgingSummary = Type === 'Receivable' ? fetchingInvoiceSummaries : fetchingBillSummaries;

  // The card's currency toggle. Nothing chosen yet falls back to the list's
  // currency filter, then the account's home currency, then whichever currency
  // has open items.
  const [chosenAgingCurrency, setChosenAgingCurrency] = useState<string | null>(null);

  const agingCurrencies: string[] = [
    ...new Set(agingSummaries.map((summary) => summary.Currency || emptyStateCurrency)),
  ];

  const agingCurrency =
    [chosenAgingCurrency, currency, emptyStateCurrency].find(
      (candidate) => candidate && agingCurrencies.includes(candidate),
    ) ||
    agingCurrencies[0] ||
    emptyStateCurrency;

  const refreshAgingSummary = async (connectionId: number) => {
    const contactScope = ContactID ? { ContactID } : {};

    try {
      if (Type === 'Receivable') {
        await fetchAspInvoicesSummary({ ConnectionID: connectionId, ...contactScope });
      } else {
        await fetchAspBillsSummary({ ConnectionID: connectionId, ...contactScope });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('loadAgingSummaryError', {
          defaultValue: 'Could not load the overview: {{errorMessage}}',
          errorMessage,
        }),
      );
    }
  };

  // The aging tile click drives the date filter; the focus treatment on the
  // date inputs shows the user where the filter landed, then fades.
  const [isDateRangeHighlighted, setIsDateRangeHighlighted] = useState(false);
  const highlightTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(highlightTimeout.current), []);

  const agingBucketSnapshot = useRef<AgingBucketSnapshot | null>(null);

  const bucketQueryParams = (bucket: AspAgingBucketKey) => {
    const [rangeStart, rangeEnd] = getAgingBucketRange(bucket);

    return { timeRange: 'custom', startDate: String(rangeStart.getTime()), endDate: String(rangeEnd.getTime()) };
  };

  const selectAgingBucket = (bucket: AspAgingBucketKey, bucketCurrency: string, extra: Partial<QueryParams> = {}) => {
    // The first selection remembers the filters it displaces; moving between
    // buckets keeps that original snapshot so deselecting lands back where the
    // user started.
    if (!queryParams.agingBucket) {
      agingBucketSnapshot.current = {
        timeRange: embeddedParams.timeRange ?? null,
        startDate: embeddedParams.startDate ?? null,
        endDate: embeddedParams.endDate ?? null,
        currency: embeddedParams.currency ?? null,
      };
    }

    // The buckets count open items, so the range always lands on the open
    // tab, narrowed to the card's currency alongside the date range.
    updateQueryParams({
      ...extra,
      tab: 'open',
      ...bucketQueryParams(bucket),
      currency: bucketCurrency,
      agingBucket: bucket,
    });

    setIsDateRangeHighlighted(true);
    clearTimeout(highlightTimeout.current);
    highlightTimeout.current = setTimeout(() => setIsDateRangeHighlighted(false), 2500);
  };

  const clearAgingBucket = (extra: Partial<QueryParams> = {}) => {
    const snapshot: AgingBucketSnapshot = agingBucketSnapshot.current ?? {
      timeRange: null,
      startDate: null,
      endDate: null,
      currency: null,
    };

    agingBucketSnapshot.current = null;

    // Functional update, and only while a bucket is still applied: the tab
    // trigger fires its change twice per click (mouse down, then focus), and
    // the second call must not restore an already-consumed snapshot's null
    // fallback over the first one's values.
    setEmbeddedParams((prev) =>
      prev.agingBucket ? { ...prev, ...snapshot, ...extra, agingBucket: null } : { ...prev, ...extra },
    );
  };

  const toggleAgingBucket = (bucket: AspAgingBucketKey, bucketCurrency: string) => {
    if (queryParams.agingBucket === bucket) {
      clearAgingBucket();
    } else {
      selectAgingBucket(bucket, bucketCurrency);
    }
  };

  // Switching the card's currency re-renders its figures for that currency and
  // drops any selected bucket (its range no longer describes what is shown).
  const handleAgingCurrencyChange = (nextCurrency: string) => {
    setChosenAgingCurrency(nextCurrency);
    if (queryParams.agingBucket) clearAgingBucket();
  };

  // Calculate dates based on timeRange, but use URL params for custom range
  const { startDate, endDate } = useMemo(() => {
    if (queryParams.timeRange === 'custom') {
      // For custom range, use the dates from URL params
      return {
        startDate: queryParams.startDate,
        endDate: queryParams.endDate,
      };
    } else {
      // For preset ranges, calculate the dates
      const { startDate: calcStart, endDate: calcEnd } = calculateDateRange(
        queryParams.timeRange,
        queryParams.startDate,
        queryParams.endDate,
      );

      return {
        startDate: calcStart?.getTime() as Timestamp | undefined,
        endDate: calcEnd?.getTime() as Timestamp | undefined,
      };
    }
  }, [queryParams.timeRange, queryParams.startDate, queryParams.endDate]);

  const [lastStartDate, setLastStartDate] = useState<Timestamp | undefined>(startDate);
  const [lastEndDate, setLastEndDate] = useState<Timestamp | undefined>(endDate);
  const [lastDateFilterType, setLastDateFilterType] = useState<DateFilterType | undefined>(undefined);

  const [showSyncStarted, setShowSyncStarted] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem' });

  // Which date the closed tab is anchored on, for the totals header suffix
  // ("Selected duration + by {date type}" - Figma).
  const closedDateTypeLabel =
    dateFilterType === 'documentDate'
      ? Type === 'Receivable'
        ? t('SearchForm.DateFilterType.Options.invoiceDate', { defaultValue: 'Invoice Date' })
        : t('SearchForm.DateFilterType.Options.billDate', { defaultValue: 'Bill Date' })
      : Type === 'Receivable'
        ? t('SearchForm.DateFilterType.Options.receivedDate', { defaultValue: 'Received Date' })
        : t('SearchForm.DateFilterType.Options.paymentDate', { defaultValue: 'Payment Date' });

  // State to hold action handlers from AccountingSystemOpen

  const titleMap: Record<AccountingType, string> = {
    Payable: t('pageTitlePayable', { defaultValue: 'Payables' }),
    Receivable: t('pageTitleReceivable', { defaultValue: 'Receivables' }),
  };

  const accountingSystemCtx: AccountingSystemCtx = {
    showSyncStarted,
    showError,
    errorMessage,
  };

  const handlers = {
    handleAccountDialogue: (handlerProps: DialogueHandlerProps<AccountingSystemDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: AccountingSystemDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowError(false);
            },
          },
        },
        sync: {
          confirm: {
            default: () => {
              setShowSyncStarted(false);
            },
          },
          fail: {
            default: () => {},
          },
        },
      };

      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };

  const [aspConnection] = connections;
  const prevTypeRef = useRef(Type);

  const loadConnections = async () => {
    try {
      const res = await fetchAspConnections();

      // No asp connection = nothing is set up — show the connect flow.
      if (!res.Connections.length) {
        setShowConnect(true);
        setIsInitialized(true);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('loadConnectionError', {
          defaultValue: 'Could not load your accounting connection: {{errorMessage}}',
          errorMessage,
        }),
      );
    }
  };

  useEffect(() => {
    loadConnections();
  }, []);

  const handleSyncClick = async () => {
    if (isSyncingData || !aspConnection) return;
    setPollCycle(0);
    setIsSyncingData(true);

    try {
      await triggerAspSync({ ConnectionID: aspConnection.ConnectionID });
      setShowSyncStarted(true);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '');
      setShowError(true);
      setIsSyncingData(false);
    }

    setFetchingStatus(true);
  };

  useEffect(() => {
    if (fetchingStatus && aspConnection) {
      const refreshStatus = async () => {
        try {
          await fetchAspSyncStatus({ ConnectionID: aspConnection.ConnectionID });
        } catch (error) {
          // The passive load-time check stays silent (the indicator just shows
          // nothing), but while the user is waiting on a manual sync a failed
          // check must surface — the 60s loop keeps retrying either way.
          if (isSyncingData) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';

            toast.neutral(
              t('syncStatusError', {
                defaultValue: 'Could not check the sync status: {{errorMessage}}',
                errorMessage,
              }),
            );
          }
        } finally {
          setPollCycle((cycle) => cycle + 1);
        }
      };

      refreshStatus();
      setFetchingStatus(false);
    }
  }, [fetchingStatus, aspConnection]);

  useEffect(() => {
    if (syncStatus) {
      // Anything that isn't still pending ends the wait — a sync that failed
      // server-side must release the button rather than poll forever.
      if (syncStatus.SyncStatus !== 'pending') {
        setIsSyncingData(false);
      }

      syncStatus.MostRecentUpdateDate &&
        setLastSync(format(parseISO(syncStatus.MostRecentUpdateDate), 'yyyy-MM-dd HH:mm:ss'));
    }
  }, [syncStatus]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    // Re-arms after every completed poll (pollCycle), not just when a sync
    // starts — otherwise polling stops after a single check and the sync
    // button stays disabled for the rest of the session.
    if (isSyncingData) {
      if (pollCycle >= MAX_SYNC_POLL_CYCLES) {
        setIsSyncingData(false);

        toast.neutral(
          t('syncTimeoutError', {
            defaultValue: 'Your accounting data is still syncing. Refresh the page in a few minutes to check again.',
          }),
        );
      } else {
        timeoutId = setTimeout(() => {
          setFetchingStatus(true);
        }, 60000);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isSyncingData, pollCycle]);

  // Filter function: Check if currency matches
  const filterByCurrency = (data: BillOrInvoiceRailz): boolean => {
    return currency === '' || currency === 'all' || currency.split('|').includes(data.currency);
  };

  // Filter function: Check if status matches (for open tab)
  const filterByStatus = (data: BillOrInvoiceRailz): boolean => {
    return status === '' || status === 'all' || data.status?.toLowerCase() === status.toLowerCase();
  };

  // Filter function: Check if search term matches
  const filterBySearchTerm = (data: BillOrInvoiceRailz, searchKeys: (keyof BillOrInvoiceRailz)[]): boolean => {
    if (searchTerm === '') return true;

    const customerName: string = data.Customer?.Name || data.customerRef?.name || data.vendorRef?.name || '';

    return (
      searchKeys.some((key) => data[key]?.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const searchTransactions = () => {
    const openItemsSearchKeys: (keyof BillOrInvoiceRailz)[] = ['invoiceNumber', 'amountDue'];
    // Equivalent keys on the row shape (the previous ones belonged to an older
    // type and never matched anything).
    const closedItemsSearchKeys: (keyof BillOrInvoiceRailz)[] = ['invoiceNumber', 'totalAmount'];

    // Filter open bills/invoices. Open rows no longer carry saved payment
    // methods (resolved lazily per action), so the payment-method filter only
    // applies to the closed tab.
    setRailzBillsOrInvoicesFiltered(
      railzBillsOrInvoices
        .filter((data) => {
          return filterByCurrency(data) && filterBySearchTerm(data, openItemsSearchKeys) && filterByStatus(data);
        })
        .sort((a, b) => compareAsc(parseISO(b.postedDate), parseISO(a.postedDate))),
    );

    // Closed rows sort on the date the tab is anchored on (Figma: the selected
    // date type drives both the filter and the sort).
    const closedAnchorDate = (data: BillOrInvoiceRailz) =>
      dateFilterType === 'documentDate' ? data.postedDate : getLastPaymentDate(data.PaymentDetails, data.postedDate);

    setBillsOrInvoicesFiltered(
      BillsOrInvoices.filter((data) => filterByCurrency(data) && filterBySearchTerm(data, closedItemsSearchKeys)).sort(
        (a, b) => closedAnchorDate(b).localeCompare(closedAnchorDate(a)),
      ),
    );
  };

  useEffect(() => {
    setBillsOrInvoicesFiltered([...BillsOrInvoices]);
    searchTransactions();
  }, [BillsOrInvoices]);

  useEffect(() => {
    setRailzBillsOrInvoicesFiltered([...railzBillsOrInvoices]);
    searchTransactions();
  }, [railzBillsOrInvoices]);

  // Trigger search when filter params change from URL
  useEffect(() => {
    if (railzBillsOrInvoices.length > 0 || BillsOrInvoices.length > 0) {
      searchTransactions();
    }
  }, [currency, searchTerm, status, dateFilterType]);

  // asp invoices/bills carry no banking data. Rows only get the contact →
  // ClientAccountID mapping here; the payment methods themselves are fetched
  // lazily (per dialog/pay action) — fetching them per contact at list load
  // was one request per customer and didn't scale.
  const buildClientAccountsByContact = async () => {
    const clientAccountByContact: { [contactId: number]: string } = {};
    if (!aspConnection) return clientAccountByContact;

    const contacts =
      Type === 'Payable'
        ? (await fetchAspVendors({ ConnectionID: aspConnection.ConnectionID })).Vendors
        : (await fetchAspCustomers({ ConnectionID: aspConnection.ConnectionID })).Customers;

    contacts.forEach((contact) => {
      if (contact.ClientAccountID) clientAccountByContact[contact.ContactID] = contact.ClientAccountID;
    });

    return clientAccountByContact;
  };

  const fetchBillsOrInvoices = async (
    accountingType: AccountingType,
    transactionsPeriod: {
      FromDate: Timestamp | undefined;
      ToDate: Timestamp | undefined;
    },
  ) => {
    // Both sides read from the asp cache; the legacy railz row shape the
    // tabs/dialogs consume is produced by the adapter.
    if (!aspConnection) return;

    const contactScope = ContactID ? { ContactID } : {};

    blockingLoader.show();

    try {
      // The unbounded range ('allTime') resolves to no dates — the asp
      // endpoints take StartDate/EndDate as optional, so omit them rather
      // than formatting an Invalid Date.
      const dateRange = {
        ...(transactionsPeriod.FromDate
          ? { StartDate: format(new Date(Number(transactionsPeriod.FromDate)), 'yyyy-MM-dd') }
          : {}),
        ...(transactionsPeriod.ToDate
          ? { EndDate: format(new Date(Number(transactionsPeriod.ToDate)), 'yyyy-MM-dd') }
          : {}),
      };

      // Closed items default to settlement-date anchoring; the user can
      // switch the closed tab to bill/invoice-date anchoring from the time filter.
      const closedDateRange = {
        ...dateRange,
        DateType: dateFilterType === 'documentDate' ? ('DueDate' as const) : ('PaymentDate' as const),
      };

      if (accountingType === 'Payable') {
        const [openBills, paidBills] = await Promise.all([
          fetchAspBills({ ConnectionID: aspConnection.ConnectionID, Status: 'open', ...dateRange, ...contactScope }),
          fetchAspBills({
            ConnectionID: aspConnection.ConnectionID,
            Status: 'paid',
            ...closedDateRange,
            ...contactScope,
          }),
        ]);

        const clientAccountByContact = await buildClientAccountsByContact();

        const toRow = (bill: AspBill) =>
          toAccountingBillRow(
            bill,
            [],
            aspConnection.ConnectionID,
            (bill.ContactID !== null && clientAccountByContact[bill.ContactID]) || null,
          );

        setRailzBillsOrInvoices(openBills.Bills.map(toRow));
        setBillsOrInvoices(paidBills.Bills.map(toRow));
      } else {
        const [openInvoices, paidInvoices] = await Promise.all([
          fetchAspInvoices({ ConnectionID: aspConnection.ConnectionID, Status: 'open', ...dateRange, ...contactScope }),
          fetchAspInvoices({
            ConnectionID: aspConnection.ConnectionID,
            Status: 'paid',
            ...closedDateRange,
            ...contactScope,
          }),
        ]);

        const clientAccountByContact = await buildClientAccountsByContact();

        const toRow = (invoice: AspInvoice) =>
          toAccountingRow(
            invoice,
            [],
            aspConnection.ConnectionID,
            (invoice.ContactID !== null && clientAccountByContact[invoice.ContactID]) || null,
          );

        setRailzBillsOrInvoices(openInvoices.Invoices.map(toRow));
        setBillsOrInvoices(paidInvoices.Invoices.map(toRow));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        accountingType === 'Payable'
          ? t('loadBillsError', { defaultValue: 'Could not load your bills: {{errorMessage}}', errorMessage })
          : t('loadInvoicesError', { defaultValue: 'Could not load your invoices: {{errorMessage}}', errorMessage }),
      );

      setRailzBillsOrInvoices([]);
      setBillsOrInvoices([]);
    } finally {
      blockingLoader.hide();
    }
  };

  useEffect(() => {
    if (!aspConnection) return;

    // GL setup is complete once both assignments exist in the asp cache.
    const checkGLAssignments = async () => {
      try {
        const [receivablesAssignment, payablesAssignment] = await Promise.all([
          fetchAspAccountAssignment({ ConnectionID: aspConnection.ConnectionID, Type: 'receivables' }),
          fetchAspAccountAssignment({ ConnectionID: aspConnection.ConnectionID, Type: 'payables' }),
        ]);

        setShowConnect(!receivablesAssignment.Account || !payablesAssignment.Account);
      } catch {
        // On failure fall back to the setup screen — same as the legacy check.
        setShowConnect(true);
      }
    };

    void checkGLAssignments();
  }, [aspConnection]);

  useEffect(() => {
    // Both sides read from the asp cache, so the first fetch waits for the
    // connection to resolve (businessAccount signals a software is connected).
    if (aspConnection) {
      fetchBillsOrInvoices(Type, {
        FromDate: startDate,
        ToDate: endDate,
      });

      refreshAgingSummary(aspConnection.ConnectionID);
      setIsInitialized(true);
    }
  }, [aspConnection]);

  useEffect(() => {
    setIsInitialized(false);

    updateQueryParams({
      tab: 'open',
      currency: null,
      searchTerm: null,
      timeRange: null,
      startDate: null,
      endDate: null,
      status: null,
      dateFilterType: null,
      agingBucket: null,
    });

    agingBucketSnapshot.current = null;
    setChosenAgingCurrency(null);
    setIsDateRangeHighlighted(false);
    clearTimeout(highlightTimeout.current);

    setRailzBillsOrInvoices([]);
    setBillsOrInvoices([]);
    setFetchingStatus(true);

    // Re-init when the section flips receivables<->payables: the old page
    // re-fetched the business account here, which re-drove its init effect;
    // the asp connection is Type-agnostic, so nothing re-runs the
    // [aspConnection] init — do it directly (mount is skipped: the
    // [aspConnection] effect owns first init).
    if (prevTypeRef.current !== Type && aspConnection) {
      fetchBillsOrInvoices(Type, {
        FromDate: startDate,
        ToDate: endDate,
      });

      refreshAgingSummary(aspConnection.ConnectionID);
      setIsInitialized(true);
    }

    prevTypeRef.current = Type;
  }, [Type]);

  // Fetch new data when date range changes
  useEffect(() => {
    if (!aspConnection) return;

    // Only fetch if the dates or the closed tab's date anchoring changed. The
    // unbounded range ('allTime') resolves to no dates at all, and that is a
    // change worth fetching for too — fetchBillsOrInvoices omits the window
    // in that case.
    if (lastStartDate !== startDate || lastEndDate !== endDate || lastDateFilterType !== dateFilterType) {
      fetchBillsOrInvoices(Type, {
        FromDate: startDate,
        ToDate: endDate,
      });

      setLastStartDate(startDate);
      setLastEndDate(endDate);
      setLastDateFilterType(dateFilterType);
    }
  }, [startDate, endDate, aspConnection, Type, dateFilterType]);

  const handleSuccess = () => {
    if (aspConnection) {
      fetchBillsOrInvoices(Type, {
        FromDate: startDate,
        ToDate: endDate,
      });

      // Payments change what's owed, so the all-time overview refreshes too.
      if (aspConnection) refreshAgingSummary(aspConnection.ConnectionID);
    }
  };

  useEffect(() => {
    // The payables flow guards against insufficient funds before paying.
    if (actionsEnabled) {
      fetchAccountBalance({ Currency: 'CAD' }).catch(() => {
        // Logged by the hook; a missing balance only affects the pay guard.
      });
    }
  }, [actionsEnabled]);

  const handleTabChange = (value: string) => {
    if (value !== 'open' && value !== 'closed') return;

    // A bucket is a filter on the open items. Leaving the open list ends it
    // and puts the filters it displaced back, so the closed list isn't left
    // narrowed to an open-items due-date window.
    if (value === 'closed' && queryParams.agingBucket) {
      clearAgingBucket({ tab: value });
    } else {
      updateQueryParams({ tab: value });
    }
  };

  const handleSearch = (params: SearchParams) => {
    // The useEffects handle fetching and filtering off the new query params.
    const nextParams = {
      currency: params.currency || null,
      searchTerm: params.searchTerm || null,
      timeRange: params.timeRange || null,
      startDate: params.startDate?.toString() || null,
      endDate: params.endDate?.toString() || null,
      status: params.status || null,
      dateFilterType: params.dateFilterType || null,
    };

    // The closed tab's filter bar has no aging select (and switching to it
    // already cleared any bucket), so its searches never touch the bucket.
    if (activeTab !== 'open') {
      updateQueryParams(nextParams);

      return;
    }

    const { searchTerm, status, dateFilterType } = nextParams;
    const previousBucket = queryParams.agingBucket;
    const nextBucket = params.agingBucket || null;

    if (nextBucket && nextBucket !== previousBucket) {
      // Picked in the filter bar: same as clicking the tile, keeping the other
      // fields the user set alongside it.
      // A currency the card can't show (no open items in it) falls back to the
      // card's currency; otherwise the card follows the bucket it filters by.
      const requestedCurrency = params.currency && params.currency !== 'all' ? params.currency : null;

      const bucketCurrency =
        requestedCurrency && agingCurrencies.includes(requestedCurrency) ? requestedCurrency : agingCurrency;

      if (bucketCurrency !== agingCurrency) setChosenAgingCurrency(bucketCurrency);
      selectAgingBucket(nextBucket, bucketCurrency, { searchTerm, status, dateFilterType });

      return;
    }

    if (!nextBucket && previousBucket) {
      // Cleared in the filter bar: back to the filters the bucket displaced —
      // except a range or currency the user changed in this same search,
      // which wins over the snapshot.
      const applied = {
        timeRange: embeddedParams.timeRange ?? null,
        startDate: embeddedParams.startDate ?? null,
        endDate: embeddedParams.endDate ?? null,
        currency: embeddedParams.currency ?? null,
      };

      const isRangeEdited =
        nextParams.timeRange !== applied.timeRange ||
        nextParams.startDate !== applied.startDate ||
        nextParams.endDate !== applied.endDate;

      const isCurrencyEdited = nextParams.currency !== applied.currency;

      clearAgingBucket({
        searchTerm,
        status,
        dateFilterType,
        ...(isRangeEdited
          ? { timeRange: nextParams.timeRange, startDate: nextParams.startDate, endDate: nextParams.endDate }
          : {}),
        ...(isCurrencyEdited ? { currency: nextParams.currency } : {}),
      });

      return;
    }

    if (nextBucket) {
      // Same bucket, but a hand-edited range or currency means the user has
      // moved on — drop the bucket rather than show a chip the table no longer
      // matches.
      const bucketParams = bucketQueryParams(nextBucket);

      const isBucketIntact =
        nextParams.timeRange === bucketParams.timeRange &&
        nextParams.startDate === bucketParams.startDate &&
        nextParams.endDate === bucketParams.endDate &&
        (nextParams.currency || '') === queryParams.currency;

      if (!isBucketIntact) agingBucketSnapshot.current = null;
      updateQueryParams({ ...nextParams, agingBucket: isBucketIntact ? nextBucket : null });

      return;
    }

    updateQueryParams(nextParams);
  };

  return (
    <AccountingSoftwareProvider
      account={account}
      userPermissions={userPermissions ?? { payAndCollect: false }}
      hostActions={hostActions ?? {}}
    >
      <AccountingSystemContext.Provider value={accountingSystemCtx}>
        {isInitialized && (
          <>
            {!showConnect && (
              <>
                {/* The account balance is deliberately not shown here — it only
                    appears when the user intends to make a payment (the pay and
                    collect dialogs; the balance still gates payables via the
                    insufficient-funds check). */}
                {!isContactScoped && (
                  <>
                    <Box flx="row" jc="sb" ai="fs">
                      <Box flx="col">
                        <Title title={titleMap[Type]} />

                        <Box flx="col" width="100%" mt="xmd" mb="xmd" base="8">
                          <p>
                            {t('pageSubTitle', {
                              defaultValue: 'In this section you will have the option to send a collection',
                            })}
                          </p>
                        </Box>
                      </Box>
                    </Box>

                    <Box jc="sb" ai="center">
                      <Box ai="center" gap="sm">
                        <Box gap="sm">
                          {syncStatus?.SyncStatus === 'success' && (
                            <Box>{t('lastSync', { defaultValue: 'Last Synced: {{lastSync}}', lastSync })}</Box>
                          )}

                          {syncStatus?.SyncStatus === 'pending' && (
                            <Box>
                              <Trans
                                ns="translations"
                                i18nKey="Shared.AccountingSystem.SyncStatus.label"
                                components={{
                                  span: <span className={styles.syncHighlight} />,
                                }}
                              />

                              <div className={styles.tooltipContianer}>
                                <Tooltip
                                  icon="question"
                                  information={t('SyncStatus.tooltip', {
                                    defaultValue:
                                      'Your accounting data is syncing to VoPay. This may take a few minutes.',
                                  })}
                                  position="topLeft"
                                />
                              </div>
                            </Box>
                          )}
                        </Box>

                        <CircleButton
                          id="Sync-Data"
                          color="secondary"
                          disabled={isSyncingData}
                          type="button"
                          icon="cached"
                          colorScheme="neutral"
                          size="large"
                          onClick={() => {
                            void handleSyncClick();
                          }}
                        />
                      </Box>

                      {activeTab === 'open' && hostActions?.onCreateDocument && (
                        <Button
                          id="create"
                          colorScheme="primary"
                          type="button"
                          onClick={() => {
                            hostActions.onCreateDocument?.(Type === 'Receivable' ? 'invoice' : 'bill');
                          }}
                        >
                          {Type === 'Receivable'
                            ? t('OpenTab.createInvoiceButton', { defaultValue: 'Create Invoice' })
                            : t('OpenTab.createBillButton', { defaultValue: 'Create Bill' })}
                        </Button>
                      )}
                    </Box>
                  </>
                )}

                <AgingOverview
                  // Remount on a section switch: the card's own collapsed state
                  // must not carry over ("collapsed on every load").
                  key={Type}
                  summaries={agingSummaries}
                  currencies={agingCurrencies}
                  type={Type}
                  emptyStateCurrency={emptyStateCurrency}
                  isFetching={fetchingAgingSummary}
                  currency={agingCurrency}
                  onCurrencyChange={handleAgingCurrencyChange}
                  selectedBucket={queryParams.agingBucket}
                  onBucketClick={toggleAgingBucket}
                />

                <Box flx="col" width="100%" mt="lg">
                  {/* Inside a contact's detail view the list card sits on the
                      drawer's sheet: flat tabs, shadow only along the card's top
                      edge (Figma: accounting tabs embedded in contacts). */}
                  <CardTabs.Root
                    value={activeTab}
                    onValueChange={handleTabChange}
                    variant={isContactScoped ? 'topShadow' : 'default'}
                  >
                    <CardTabs.List aria-label={t('tabsLabel', { defaultValue: 'Accounting Software' })}>
                      <CardTabs.Trigger value="open">{t('openTab', { defaultValue: 'Outstanding' })}</CardTabs.Trigger>
                      <CardTabs.Trigger value="closed">{t('closedTab', { defaultValue: 'Closed' })}</CardTabs.Trigger>
                    </CardTabs.List>

                    <CardTabs.Content value="open">
                      <div className={styles.tableHeaderCard}>
                        <TableFiltersContainer filtersApplied>
                          <div className={styles.tableHeaderTop}>
                            <AccountingSystemTotals
                              billsOrInvoices={railzBillsOrInvoicesFiltered}
                              type={Type}
                              activeTab="open"
                              emptyStateCurrency={emptyStateCurrency}
                              startDate={startDate}
                              endDate={endDate}
                            />

                            <div className={styles.tableHeaderActions}>
                              <AccountingSystemExport
                                billsOrInvoices={railzBillsOrInvoicesFiltered}
                                type={Type}
                                activeTab="open"
                                dateFilterType={dateFilterType}
                                serviceName={aspConnection?.ServiceName || ''}
                              />

                              <span className={styles.actionsDivider} />
                              <TableFiltersContainer.Trigger />
                            </div>
                          </div>

                          <TableFiltersContainer.Content>
                            <AccountingSystemSearchForm
                              section={Type}
                              country={country}
                              timeRange={timeRange}
                              startDate={startDate}
                              endDate={endDate}
                              currency={currency}
                              searchTerm={searchTerm}
                              status={status}
                              dateFilterType={dateFilterType}
                              agingBucket={queryParams.agingBucket ?? ''}
                              activeTab={activeTab}
                              isDateRangeHighlighted={isDateRangeHighlighted}
                              isCurrencyHighlighted={isDateRangeHighlighted}
                              onSearch={handleSearch}
                            />
                          </TableFiltersContainer.Content>
                        </TableFiltersContainer>
                      </div>

                      <AccountingSystemOpen
                        onSuccess={handleSuccess}
                        BillsOrInvoices={railzBillsOrInvoicesFiltered}
                        ServiceName={aspConnection?.ServiceName || ''}
                        Type={Type}
                        AccountBalance={balanceResponse}
                        AspConnectionID={aspConnection?.ConnectionID}
                        showContactColumn={!isContactScoped}
                        onContactClick={isContactScoped ? undefined : setContactDrawerAccountId}
                      />
                    </CardTabs.Content>

                    <CardTabs.Content value="closed">
                      <div className={styles.tableHeaderCard}>
                        <TableFiltersContainer filtersApplied>
                          <div className={styles.tableHeaderTop}>
                            <AccountingSystemTotals
                              billsOrInvoices={BillsOrInvoicesFiltered}
                              type={Type}
                              activeTab="closed"
                              emptyStateCurrency={emptyStateCurrency}
                              dateTypeLabel={closedDateTypeLabel}
                              startDate={startDate}
                              endDate={endDate}
                            />

                            <div className={styles.tableHeaderActions}>
                              <AccountingSystemExport
                                billsOrInvoices={BillsOrInvoicesFiltered}
                                type={Type}
                                activeTab="closed"
                                dateFilterType={dateFilterType}
                                serviceName={aspConnection?.ServiceName || ''}
                              />

                              <span className={styles.actionsDivider} />
                              <TableFiltersContainer.Trigger />
                            </div>
                          </div>

                          <TableFiltersContainer.Content>
                            <AccountingSystemSearchForm
                              section={Type}
                              country={country}
                              timeRange={timeRange}
                              startDate={startDate}
                              endDate={endDate}
                              currency={currency}
                              searchTerm={searchTerm}
                              status={status}
                              dateFilterType={dateFilterType}
                              activeTab={activeTab}
                              onSearch={handleSearch}
                            />
                          </TableFiltersContainer.Content>
                        </TableFiltersContainer>
                      </div>

                      <AccountingSystemClosed
                        ServiceName={aspConnection?.ServiceName || ''}
                        Type={Type}
                        DateFilterType={dateFilterType}
                        showContactColumn={!isContactScoped}
                        onContactClick={isContactScoped ? undefined : setContactDrawerAccountId}
                        BillsOrInvoices={BillsOrInvoicesFiltered}
                      />
                    </CardTabs.Content>
                  </CardTabs.Root>
                </Box>
              </>
            )}

            {showConnect &&
              !isContactScoped &&
              (connect ? (
                <ConnectAccountingSoftware
                  railzWidgetId={connect.railzWidgetId}
                  businessName={account.AccountID || ''}
                  redirectURL={connect.redirectURL}
                  onCompleteChartOfAccount={() => {
                    // Setup finished — a fresh connections fetch flips the GL
                    // check and boots the lists.
                    setShowConnect(false);
                    loadConnections();
                  }}
                />
              ) : (
                <Box width="100%" jc="center" mt="xl">
                  <p>
                    {t('Embed.connectInPortal', {
                      defaultValue: 'Connect your accounting software from the VoPay portal to use this page.',
                    })}
                  </p>
                </Box>
              ))}
          </>
        )}

        {!isContactScoped && (
          <ContactDetailDrawer
            clientAccountId={contactDrawerAccountId}
            clientAccountsEnabled={Boolean(account.ClientAccountsEnabled)}
            accounting={{ account, userPermissions, hostActions }}
            onClose={() => {
              setContactDrawerAccountId(undefined);
              // Payments made inside the drawer's embedded tabs change this
              // page's lists too — refresh them on the way out.
              handleSuccess();
            }}
          />
        )}

        <AccountingSystemDialogue handleDialogue={handlers.handleAccountDialogue} />
      </AccountingSystemContext.Provider>
    </AccountingSoftwareProvider>
  );
};

export default AccountingSystemContainer;
